import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { LocaleService } from '@sm360/widget-commons-core-services';
import {
    AccountService,
    ModalService,
    UtilsService as CommonUtilsService,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import ConditionReport from '@sm360/condition-report';
import NewTradeInContext from 'trade-in-360/NewTradeInContext.js';
import ActionButton from 'trade-in-360/components/InstantEstimateWrapper/Components/ActionButton/ActionButton.jsx';

const { QuestionsListHelpers, Models } = ConditionReport;

const SignUpModal = ModalService.get('login/SignUpModal');

const TradeInCta = ({
    saveTradeIn,
    appointmentDate,
    onMakeAppointment,
    officialAnswers,
    onOfficialEvaluation,
    fetching,
    isMainCTA,
    className,
    isSaveEstimateCTA,
    shouldForceSaveOnCTA,
    onSaveEstimate,
    setLoadingOnLoginClose,
    isOnlyEmailSendCTA,
}) => {
    const { setIsLoginModalOpen, isViewOnly } = useContext(NewTradeInContext);

    const onLoginClose = useCallback(
        () => {
            setIsLoginModalOpen(false);
            // call to display loading screen since have timeout to save tradeIn
            if (AccountService.isAuthenticated()) {
                if (setLoadingOnLoginClose) setLoadingOnLoginClose();
                // Setup timeout to comply with lead creation on signup and on tradein
                setTimeout(() => {
                    saveTradeIn({ existingAccount: true, onSaveCallback: onOfficialEvaluation });
                }, 1000);
            }
            return true;
        },
        [saveTradeIn, onOfficialEvaluation]
    );

    const loginToSave = () => {
        SignUpModal.addCallbacks({
            onClose: onLoginClose,
        });
        SignUpModal.open({ context: 'widget.login.signup.information', hasConfirmation: false });
        setIsLoginModalOpen(true);
    };

    const loginToOfficialize = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_GET_TRADE_ESTIMATE_OFFICIAL_EVALUATION());
        if (!AccountService.isAuthenticated()) {
            loginToSave();
        } else {
            if (shouldForceSaveOnCTA) saveTradeIn({ existingAccount: true });
            onOfficialEvaluation();
        }
    };

    const loginToSaveEstimate = () => {
        if (!AccountService.isAuthenticated()) {
            loginToSave();
        } else {
            if (shouldForceSaveOnCTA) saveTradeIn({ existingAccount: true });
            onSaveEstimate();
        }
    };

    const handleMakeAppointment = () => {
        if (AccountService.isAuthenticated()) {
            if (shouldForceSaveOnCTA) saveTradeIn({ existingAccount: true });
        }
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_BOOK_AN_APPOINTMENT_BTN());
        onMakeAppointment();
    };

    const officialEvaluationStatus = QuestionsListHelpers.getQuestionnaireProgressStatus(
        officialAnswers,
        Models.DeskingQuestionsModel
    );
    const officialEvaluationLabel = CommonUtilsService.getOfficialEvaluationLabel(officialEvaluationStatus, isViewOnly);

    const shouldRenderAppointmentCTA = onMakeAppointment && !isViewOnly;
    const shouldRenderOfficialEvaluationCTA = onOfficialEvaluation;
    const shouldRenderSaveEstimateCTA = isSaveEstimateCTA;

    const shouldRenderOnlyEmailSendCTA = isOnlyEmailSendCTA;

    if (
        !shouldRenderAppointmentCTA &&
        !shouldRenderOfficialEvaluationCTA &&
        !shouldRenderSaveEstimateCTA &&
        !shouldRenderOnlyEmailSendCTA
    )
        return null;

    return (
        <div className={`vehicle-buttons ${className}`}>
            {shouldRenderAppointmentCTA && (
                <ActionButton
                    noPrefix
                    noArrow
                    noSubmit
                    onClick={handleMakeAppointment}
                    loading={fetching}
                    disabled={appointmentDate}
                >
                    {LocaleService.t('TRADE_IN.BUTTONS.REQUEST_APPOINTMENT')}
                </ActionButton>
            )}
            {shouldRenderOfficialEvaluationCTA && (
                <ActionButton
                    next={isMainCTA}
                    noPrefix
                    noArrow
                    noSubmit
                    onClick={loginToOfficialize}
                    loading={fetching}
                    classesName={['get-official']}
                    right={isViewOnly}
                >
                    {officialEvaluationLabel}
                </ActionButton>
            )}
            {shouldRenderSaveEstimateCTA && (
                <ActionButton
                    next={isMainCTA}
                    disabled={!onSaveEstimate}
                    noPrefix
                    noArrow
                    onClick={loginToSaveEstimate}
                >
                    {LocaleService.t('TRADE_IN.BUTTONS.SAVE_ESTIMATE')}
                </ActionButton>
            )}
            {shouldRenderOnlyEmailSendCTA && (
                <ActionButton
                    next={isMainCTA}
                    disabled={!onSaveEstimate}
                    noPrefix
                    noArrow
                    onClick={() => saveTradeIn({ existingAccount: false })}
                >
                    {LocaleService.t('TRADE_IN.BUTTONS.SEND_ME_AN_EMAIL')}
                </ActionButton>
            )}
        </div>
    );
};

TradeInCta.defaultProps = {
    saveTradeIn: ({ onSaveCallback }) => onSaveCallback(),
    appointmentDate: '',
    onMakeAppointment: undefined,
    officialAnswers: {},
    onOfficialEvaluation: undefined,
    fetching: false,
    className: '',
    isSaveEstimateCTA: false,
    isOnlyEmailSendCTA: false,
    updateTradeIn: undefined,
    onSaveEstimate: undefined,
    shouldForceSaveOnCTA: false, // To save updated tradeIn if True (In the case if estimated tradeIn amount is updated by slider)
};

TradeInCta.propTypes = {
    saveTradeIn: PropTypes.func,
    appointmentDate: PropTypes.string,
    onMakeAppointment: PropTypes.func,
    officialAnswers: PropTypes.object,
    onOfficialEvaluation: PropTypes.func,
    fetching: PropTypes.bool,
    className: PropTypes.string,
    isSaveEstimateCTA: PropTypes.bool,
    isOnlyEmailSendCTA: PropTypes.bool,
    shouldForceSaveOnCTA: PropTypes.bool,
    onSaveEstimate: PropTypes.func,
};

TradeInCta.displayName = 'TradeInCta';
export default TradeInCta;
